import React, { useEffect, useState } from "react";
import { Page } from "components/Page";
import { useGlobalContext } from "state/GlobalContext";
import { Form } from "components/common/forms";
import { Discount, PaymentMethod, Quote, Reservation } from "models";
import { useNavigate } from "react-router-dom";
import { CardBox } from "page-components/CardBox";
import { ActionButton } from "page-components/ActionButton";
import { ChargeDetails } from "page-components/ChargeDetails";
import { Paragraph } from "theme-ui";
import Services from "services/Services";
import useRequest from "hooks/useRequest";
import CreditCardSection from "./CreditCardSection";
import DiscountSection from "./DiscountSection";
import AddressSection from "./AddressSection";
import ErrorSection from "page-components/ErrorSection";
import { Address, addressFields, BillingAddress, billingFields } from "models/Address";
import useMount from "hooks/useMount";
import { CheckboxControl } from "page-components/CheckboxControl";
import GuestForm from "./GuestForm";
type FormValues = PaymentMethod & Discount & BillingAddress;

const PaymentPage: React.FC = () => {
  const fields = PaymentMethod.Model.fields;
  const navigate = useNavigate();
  const [{ state, dispatch }] = useGlobalContext();
  const rateId = state.quote?.rateId;
  const [form] = Form.useForm<FormValues>();
  const showBilling = !Form.useWatch(fields.useMailingAsBilling.name, form);
  const [canPay, setCanPay] = useState(false);

  useMount(() => {
    form.setFieldsValue({ useMailingAsBilling: true });
  });

  const createPaymentMethod = async () => {
    const values = form.getFieldsValue();
    if (values.cardNumber && values.expiry && values.cvc) {
      const payload = PaymentMethod.Model.buildPayload(values, state.guest);
      const response = await Services.PaymentMethods.createOrUpdate(payload);
      return response;
    }
  }

  const [createReservation] = useRequest(async () => {
    const reservation = state.reservation;
    let paymentMethodId = reservation.paymentMethodId;
    if (!paymentMethodId) {
      const paymentMethod = await createPaymentMethod();
      paymentMethodId = paymentMethod.id;
      reservation.paymentMethodId = paymentMethodId;
      dispatch({ type: 'SET_RESERVATION', payload: { reservation: reservation }});
    }
    if (paymentMethodId) {
      reservation.guestId = state.guest.id;
      const payload = Reservation.Model.buildPayload(state.reservation, state.selectedPark.id);
      const response = await Services.Reservations.createOrUpdate(payload);
      return response;
    }
  })

  useEffect(() => {
    const values = form.getFieldsValue();
    updateCanPay({...values, ...state.guest});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.guest, form]);

  const onValuesChange = (changedValues: Partial<FormValues>, values: FormValues) => {
    const fieldName = Object.keys(changedValues)[0];
    const creditCardFields = [fields.cardNumber, fields.expiry, fields.cvc];
    const addressFieldsList = Object.entries(addressFields).map(entry => entry[1]);
    const billingFieldsList = Object.entries(billingFields).map(entry => entry[1]);
    const fieldList = [fields.useMailingAsBilling, ...creditCardFields, ...addressFieldsList, ...billingFieldsList];
    if (fieldList.some(f => f.name === fieldName)) {
      setTimeout(() => updateCanPay(values), 10);
    }
  };

  const updateCanPay = (values: Partial<FormValues & Address>) => {
    const reqFields = [fields.cardNumber, fields.expiry, fields.cvc];
    const addressFieldsList = Object.entries(addressFields).map(entry => entry[1]).filter(x => x.name !== 'address2');
    const billingFieldsList = Object.entries(billingFields).map(entry => entry[1]).filter(x => x.name !== 'billingAddress2');
    const cardIsFilled = true && values.cardNumber && values.expiry?.length === 5 && values.cvc?.length >= 3;

    const errors = form.getFieldsError();
    const cardErrors = errors.filter(err => reqFields.some(field => field.name === err.name.toString()));
    const hasError = cardErrors.some(err => err.errors.length > 0);
    
    const addressValues = addressFieldsList.map(field => state.guest?.[field.name]);
    let hasAddress = addressValues.every(val => val?.length > 0);
    if (!values.useMailingAsBilling) {
      const billingValues = billingFieldsList.map(field => values[field.name]);
      hasAddress = billingValues.every(val => val?.length > 0);
    }
    setCanPay(cardIsFilled && !hasError && hasAddress);
  }

  const onDiscountChange = async (discount: Discount) => {
    const reservation = state.reservation;
    reservation.discountId = discount.discountId;
    reservation.discountCode = discount.discountCode;
    reservation.loyaltyNumber = discount.loyaltyNumber;
    const payload = Quote.Model.buildPayload(reservation);
    const result = await Services.Reservations.getQuote(payload);
    if (result) {
      const quote = Quote.Model.mapModel(result);
      dispatch({ type: 'SET_QUOTE', payload: { quote: quote }});
      dispatch({ type: 'SET_RESERVATION', payload: { reservation: reservation }});
    }
  }

  const onPayCard = async () => {
    await createReservation.execute();
    if (createReservation.result) {
      navigate('/confirmation');
    }
  }

  return (
    <Page title="Payment">
      <CardBox md={14}>
        <GuestForm guest={state.guest} parkId={state.selectedPark?.id} />
      </CardBox>

      <CardBox md={10}>
        <Form form={form}
          onValuesChange={onValuesChange}
          layout="vertical"
          autoComplete="off"
        >
          <ChargeDetails title="Complete Payment" quote={state.quote} />
          <DiscountSection rateId={rateId} reservation={state.reservation} onChange={onDiscountChange} />
          <Paragraph sx={{fontSize: 1}} my={3}>
            *Total amount may not include applicable fees.<br/>
            Your balance will be due at check-in.<br/>
          </Paragraph>

          <CreditCardSection form={form} />
          <Form.Item name={fields.useMailingAsBilling.name}>
            <CheckboxControl label={fields.useMailingAsBilling.label} />
          </Form.Item>
          {showBilling && (
            <AddressSection parentForm={form} initialValues={state?.guest} fieldSet={billingFields} />
          )}
          {createReservation.error && (
            <ErrorSection error={createReservation.error} />
          )}
          <ActionButton onClick={onPayCard} disabled={!canPay} loading={createReservation.status === 'pending'}>
            Pay with Card
          </ActionButton>
        </Form>
      </CardBox>
    </Page>
  );
};

export default PaymentPage;
